import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import * as Icons from "lucide-react";

const STATUS = {
  nouvelle: { label: "Nouvelle", cls: "bg-amber-100 text-amber-700" },
  accepted: { label: "Acceptée", cls: "bg-emerald-100 text-emerald-700" },
  refusee: { label: "Refusée", cls: "bg-rose-100 text-rose-700" },
};

const tsfr = (s) => {
  if (!s) return "";
  const d = new Date(s);
  return isNaN(d) ? "" : d.toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
};

export default function ClientRequests() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState("");
  const [rejectReq, setRejectReq] = useState(null);
  const [pDate, setPDate] = useState("");
  const [pMsg, setPMsg] = useState("");
  const [replyDraft, setReplyDraft] = useState({});
  const [delReq, setDelReq] = useState(null);

  const load = () => api.get("/track/client-requests").then((r) => setRows(r.data)).catch((e) => { toast.error(apiError(e)); setRows([]); });
  useEffect(() => { load(); }, []);

  const groups = useMemo(() => {
    const m = new Map();
    (rows || []).forEach((r) => {
      const key = r.client_name || "Client inconnu";
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(r);
    });
    return [...m.entries()];
  }, [rows]);

  const accept = async (r) => {
    setBusy(r.id);
    try {
      const { data } = await api.post(`/track/client-requests/${r.id}/accept`);
      toast.success(`Intervention planifiée créée (${data.reference})`);
      window.dispatchEvent(new Event("cs-req-changed"));
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const openReject = (r) => { setRejectReq(r); setPDate(""); setPMsg(""); };
  const confirmReject = async () => {
    if (!rejectReq) return;
    setBusy(rejectReq.id);
    try {
      await api.post(`/track/client-requests/${rejectReq.id}/reject`, { proposed_date: pDate || null, message: pMsg || "" });
      toast.success(pDate ? "Demande refusée — nouvelle date proposée au client" : "Demande refusée");
      window.dispatchEvent(new Event("cs-req-changed"));
      setRejectReq(null);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const sendReply = async (r) => {
    const text = (replyDraft[r.id] || "").trim();
    if (!text) { toast.error("Écrivez une réponse"); return; }
    setBusy(r.id);
    try {
      const { data } = await api.post(`/track/client-requests/${r.id}/reply`, { message: text });
      toast.success(data.emailed?.length ? `Réponse envoyée au client (${data.emailed.length} e-mail)` : "Réponse enregistrée (aucune adresse e-mail client)");
      setReplyDraft((m) => ({ ...m, [r.id]: "" }));
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const confirmDelete = async () => {
    if (!delReq) return;
    setBusy(delReq.id);
    try {
      await api.delete(`/track/client-requests/${delReq.id}`);
      toast.success("Demande supprimée");
      window.dispatchEvent(new Event("cs-req-changed"));
      setDelReq(null);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  return (
    <div className="space-y-5" data-testid="client-requests-page">
      <div>
        <h1 className="font-display text-2xl font-bold text-slate-900">Demandes clients</h1>
        <p className="text-sm text-slate-500">Demandes et questions envoyées depuis l'espace client, regroupées par client. Répondez à une question : le client reçoit votre réponse par e-mail et voit l'ensemble des échanges dans son espace.</p>
      </div>

      {rows === null ? (
        <div className="flex justify-center py-16"><Icons.Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>
      ) : rows.length === 0 ? (
        <Card className="p-10 text-center text-sm text-slate-400" data-testid="cr-empty">Aucune demande client pour le moment.</Card>
      ) : (
        <div className="space-y-7">
          {groups.map(([clientName, items]) => (
            <section key={clientName} data-testid={`cr-group-${clientName}`}>
              <div className="mb-3 flex items-center gap-2">
                <Icons.Building2 className="h-4 w-4 text-emerald-600" />
                <h2 className="font-display text-base font-semibold text-slate-800">{clientName}</h2>
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">{items.length}</span>
              </div>
              <div className="space-y-3">
                {items.map((r) => {
                  const st = STATUS[r.status] || STATUS.nouvelle;
                  const awaitingClient = r.status === "refusee" && r.proposed_date && !r.client_date_response;
                  const isQuestion = r.kind === "question_devis" || !r.requested_date;
                  return (
                    <Card key={r.id} className="p-4" data-testid={`cr-${r.id}`}>
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${st.cls}`}>{st.label}</span>
                            {r.kind === "question_devis" && <span className="rounded-full bg-sky-100 px-2.5 py-0.5 text-xs font-medium text-sky-700">Question devis</span>}
                            {awaitingClient && <span className="rounded-full bg-sky-100 px-2.5 py-0.5 text-xs font-medium text-sky-700">En attente du client</span>}
                            {r.client_date_response === "accepted" && <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-700">Date acceptée</span>}
                            {r.client_date_response === "declined" && <span className="rounded-full bg-rose-100 px-2.5 py-0.5 text-xs font-medium text-rose-700">Date refusée</span>}
                            {r.subject && <span className="font-semibold text-slate-800">{r.subject}</span>}
                            <span className="flex items-center gap-1 text-sm text-slate-500"><Icons.MapPin className="h-3.5 w-3.5" />{r.site_display}
                              {r.is_new_site && <span className="ml-1 rounded bg-lime-100 px-1.5 py-0.5 text-[11px] font-medium text-lime-700">Nouveau site</span>}</span>
                          </div>
                          <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-600">
                            {r.requested_date && <span className="flex items-center gap-1"><Icons.Calendar className="h-3.5 w-3.5 text-slate-400" />Souhaité le {r.requested_date}</span>}
                            {r.proposed_date && <span className="flex items-center gap-1 text-amber-700"><Icons.CalendarClock className="h-3.5 w-3.5" />Date proposée : {r.proposed_date}</span>}
                            {r.prestation_label && <span className="flex items-center gap-1"><Icons.Sparkles className="h-3.5 w-3.5 text-slate-400" />{r.prestation_label}</span>}
                            <span className="flex items-center gap-1 text-slate-400"><Icons.Mail className="h-3.5 w-3.5" />{r.via_client_email}</span>
                          </div>
                        </div>
                        <div className="flex shrink-0 gap-2">
                          {r.status === "accepted" && r.worksite_id && (
                            <Button size="sm" variant="outline" onClick={() => navigate(`/track/${r.worksite_id}`)} data-testid={`cr-view-${r.id}`}>Voir le chantier</Button>
                          )}
                          {r.status === "nouvelle" && r.requested_date && (
                            <>
                              <Button size="sm" variant="outline" className="text-rose-600" disabled={busy === r.id} onClick={() => openReject(r)} data-testid={`cr-reject-${r.id}`}>Refuser</Button>
                              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={busy === r.id} onClick={() => accept(r)} data-testid={`cr-accept-${r.id}`}>
                                {busy === r.id ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : "Accepter & planifier"}
                              </Button>
                            </>
                          )}
                          <Button size="sm" variant="ghost" className="text-rose-500" disabled={busy === r.id} onClick={() => setDelReq(r)} data-testid={`cr-delete-${r.id}`}><Icons.Trash2 className="h-4 w-4" /></Button>
                        </div>
                      </div>

                      {/* Fil des échanges */}
                      <div className="mt-3 space-y-2 border-t border-slate-100 pt-3" data-testid={`cr-thread-${r.id}`}>
                        {(r.messages || []).map((m, i) => (
                          <div key={i} className={`flex ${m.author === "admin" ? "justify-end" : "justify-start"}`} data-testid={`cr-thread-${r.id}-msg-${i}`}>
                            <div className={`max-w-[85%] rounded-2xl px-4 py-2 text-sm ${m.author === "admin" ? "bg-emerald-50 text-emerald-900" : "bg-slate-100 text-slate-700"}`}>
                              <div className="mb-0.5 text-[11px] font-medium opacity-70">{m.author === "admin" ? (m.author_name || "SIGNA CLEAN") : "Client"}{m.at ? ` · ${tsfr(m.at)}` : ""}</div>
                              {m.text}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Répondre */}
                      <div className="mt-3 flex items-end gap-2">
                        <Textarea value={replyDraft[r.id] || ""} onChange={(e) => setReplyDraft((m) => ({ ...m, [r.id]: e.target.value }))} rows={2}
                          placeholder="Répondre au client…" className="flex-1" data-testid={`cr-reply-input-${r.id}`} />
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={busy === r.id} onClick={() => sendReply(r)} data-testid={`cr-reply-send-${r.id}`}>
                          {busy === r.id ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <><Icons.Send className="mr-1.5 h-4 w-4" />Répondre</>}
                        </Button>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}

      <Dialog open={!!rejectReq} onOpenChange={(o) => !o && setRejectReq(null)}>
        <DialogContent data-testid="cr-reject-dialog">
          <DialogHeader>
            <DialogTitle>Refuser la demande</DialogTitle>
            <DialogDescription>Vous pouvez proposer une autre date : le client recevra un e-mail et pourra l'accepter ou la refuser depuis son espace. Laissez la date vide pour simplement refuser.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Nouvelle date proposée (optionnel)</Label>
              <input type="date" value={pDate} onChange={(e) => setPDate(e.target.value)} className="flex h-10 w-full rounded-md border border-slate-200 px-3 text-sm" data-testid="cr-reject-date" />
            </div>
            <div className="space-y-1.5">
              <Label>Message au client (optionnel)</Label>
              <Textarea value={pMsg} onChange={(e) => setPMsg(e.target.value)} rows={3} placeholder="Ex. La date souhaitée n'est pas disponible, nous vous proposons…" data-testid="cr-reject-message" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectReq(null)}>Annuler</Button>
            <Button className="bg-rose-600 hover:bg-rose-700" disabled={busy === rejectReq?.id} onClick={confirmReject} data-testid="cr-reject-confirm">
              {busy === rejectReq?.id ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : (pDate ? "Refuser et proposer la date" : "Refuser la demande")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!delReq} onOpenChange={(o) => !o && setDelReq(null)}>
        <DialogContent data-testid="cr-delete-dialog">
          <DialogHeader>
            <DialogTitle>Supprimer la demande</DialogTitle>
            <DialogDescription>La demande et l'ensemble de ses échanges seront définitivement supprimés.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelReq(null)}>Annuler</Button>
            <Button className="bg-rose-600 hover:bg-rose-700" disabled={busy === delReq?.id} onClick={confirmDelete} data-testid="cr-delete-confirm">
              {busy === delReq?.id ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : "Supprimer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
